import { Component, Input, OnDestroy, TemplateRef } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';

@Component({
    selector: 'app-modal-pdf-viewer',
    templateUrl: './modal-pdf-viewer.component.html',
    styleUrls: ['./modal-pdf-viewer.component.css']
})
export class ModalPdfViewerComponent implements OnDestroy {

    @Input() pdfName = '';
    @Input() downloadLabelKey = 'delivery-note.download_document';
    @Input() showTerms = false;
    @Input() htmlTemplate?: TemplateRef<any>;
    @Input() downloadFn?: () => void;

    pdfSrc: Uint8Array | string | null = null;
    isLoading = false;
    loadError = false;
    totalPages = 0;
    currentPage = 1;
    pages: number[] = [];
    thumbnails: string[] = [];

    private _blobUrl = '';
    private _rawBlob: Blob | null = null;
    private _destroyed = false;
    private _abortController: AbortController | null = null;

    constructor(public bsModalRef: BsModalRef) {}

    @Input() set pdfBlob(blob: Blob) {
        this._rawBlob = blob;
        this.loadError = false;
        this.isLoading = true;
        blob.arrayBuffer().then(buf => {
            if (this._destroyed) return;
            this.pdfSrc = new Uint8Array(buf);
        });
    }

    @Input() set pdfUrl(url: string) {
        if (!url) return;
        this.loadError = false;
        this.isLoading = true;

        this._abortController?.abort();
        this._abortController = new AbortController();

        fetch(url, { method: 'GET', mode: 'cors', signal: this._abortController.signal })
            .then(r => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.arrayBuffer();
            })
            .then(buf => {
                if (this._destroyed) return;
                this._rawBlob = new Blob([buf], { type: 'application/pdf' });
                this.pdfSrc = new Uint8Array(buf);
            })
            .catch(e => {
                if (e.name === 'AbortError') return;
                console.error('[ModalPdfViewer] fetch error', e);
                this.loadError = true;
                this.isLoading = false;
            });
    }

    onPdfLoaded(pdf: any): void {
        this.totalPages = pdf.numPages;
        this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        this.thumbnails = new Array(this.totalPages).fill('');
        this.currentPage = 1;
        this.isLoading = false;
        this.loadError = false;
        this.generateThumbnails(pdf);
        setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 150);
    }

    onPdfError(error: unknown): void {
        console.error('[ModalPdfViewer] render error', error);
        this.loadError = true;
        this.isLoading = false;
    }

    goToPage(page: number): void {
        this.currentPage = page;
    }

    prevPage(): void {
        if (this.currentPage > 1) this.currentPage--;
    }

    nextPage(): void {
        if (this.currentPage < this.totalPages) this.currentPage++;
    }

    close(): void {
        this.bsModalRef.hide();
    }

    download(): void {
        if (this.downloadFn) {
            this.downloadFn();
            return;
        }
        const blob = this._rawBlob;
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = this.pdfName || `document-${Date.now()}.pdf`;
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    ngOnDestroy(): void {
        this._destroyed = true;
        this._abortController?.abort();
        if (this._blobUrl) URL.revokeObjectURL(this._blobUrl);
    }
    private async generateThumbnails(pdf: any): Promise<void> {
        for (let i = 1; i <= pdf.numPages; i++) {
            if (this._destroyed) break;
            try {
                const page = await pdf.getPage(i);
                const viewport = page.getViewport({ scale: 0.35 });
                const canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const ctx = canvas.getContext('2d')!;
                await page.render({ canvasContext: ctx, viewport }).promise;
                this.thumbnails[i - 1] = canvas.toDataURL('image/jpeg', 0.75);
            } catch (e) {
                console.warn('[ModalPdfViewer] thumbnail error page', i, e);
            }
        }
    }
}
